import React, { useRef, useState, useEffect } from 'react';
import { Layout, Button, Avatar, theme, Segmented, Divider, FloatButton, Col, Row, Modal, Typography, message } from 'antd';
import { FileAddOutlined, LogoutOutlined, UserOutlined, ScheduleOutlined, ContactsOutlined, CalendarOutlined, UserAddOutlined, NotificationOutlined, RobotOutlined, BellOutlined } from '@ant-design/icons';
import Tasks from '../components/Tasks';
import TaskModal from '../components/TaskModal';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import { API_URL, ADMIN } from '../config/Config.js';
import openNotificationWithIcon from '../services/notificationService';
import Profile from '../components/Profile';
import Users from '../components/Users';
import UserModal from '../components/UserModal';
import Statistics from '../components/Statistics';
import Notification from '../components/Notification';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import NotificationModal from '../components/NotificationModal.js';
import { checkTokenExpiration } from '../services/expirationService.js';
import EventModal from '../components/EventModal';
import { getPlatformInfo } from '../utils/deviceHelper';
import { requestForToken } from '../services/firebase';

const { Header, Content } = Layout;
const { Text } = Typography;

dayjs.locale('tr');
dayjs.extend(utc);
dayjs.extend(timezone);

const HomePage = () => {
  const { user, setUser } = useUser();
  const [table, setTable] = useState("tasks");
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [futureEventCount, setFutureEventCount] = useState(0);
  const [events, setEvents] = useState([]);

  // --- RESPONSIVE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- CSS STYLES (GÜNCELLENDİ: Sütun Aralarına Boşluk Eklendi) ---
  const mobileStyles = `
    @media (max-width: 900px) {
      /* Tabloyu sarmalayan div için yatay kaydırma */
      .mobile-scroll-container {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
        padding-bottom: 10px; /* Scrollbar için alt boşluk */
      }
      
      /* Tablonun kendisini daha geniş olmaya zorluyoruz. 
         Böylece sütunlar sıkışmaz, scroll devreye girer.
         650px yerine 900px yaptık.
      */
      .mobile-scroll-container table {
        min-width: 900px !important; 
      }

      /* Hücre Paddingleri: 
         Önceden "8px 4px" idi (çok sıkışıktı).
         Şimdi "10px 12px" yaptık (sütunlar arası açıldı).
      */
      .mobile-scroll-container .ant-table-cell {
        padding: 10px 12px !important;
        font-size: 12px !important; /* Fontu biraz büyüttük, okunabilir olsun */
      }

      /* Tahmini Bitiş Tarihi Sütunu */
      .mobile-scroll-container .ant-table-tbody > tr > td:nth-child(4) {
        font-size: 11px !important;
        white-space: nowrap !important;
        color: #666;
      }

      /* Metinleri kısaltma (Ellipsis) */
      .mobile-scroll-container .ant-typography, 
      .mobile-scroll-container span {
        font-size: 12px !important;
      }
    }
  `;
  // -------------------------------------------------------------------

  // --- iOS Bildirim Popup State'leri ---
  const [isNotifyModalVisible, setIsNotifyModalVisible] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  useEffect(() => {
    const checkNotificationPrompt = () => {
      const { platform, isStandalone, isIos } = getPlatformInfo();
      const isRegistered = localStorage.getItem('notification_registered') === 'true';
      if (isIos && isStandalone && !isRegistered) {
        setIsNotifyModalVisible(true);
      }
    };
    if (user) {
      checkNotificationPrompt();
    }
  }, [user]);

  const saveTokenToBackend = async (fcmToken) => {
    const { platform, deviceId } = getPlatformInfo();
    const payload = {
      userId: user.id.toString(),
      deviceId: deviceId,
      fcmToken: fcmToken,
      platform: platform
    };

    return axios.post(API_URL + "Api/Fcm/save-token", payload, {
      headers: { Authorization: "Bearer " + user.token }
    });
  };

  const handleAllowNotifications = async () => {
    setNotifyLoading(true);
    try {
      const fcmToken = await requestForToken();
      if (fcmToken) {
        await saveTokenToBackend(fcmToken);
        localStorage.setItem('notification_registered', 'true');
        message.success("Bildirimler başarıyla aktif edildi!");
        setIsNotifyModalVisible(false);
      } else {
        message.warning("Bildirim izni reddedildi.");
      }
    } catch (error) {
      console.error("Bildirim kaydı hatası:", error);
      message.error("İzin alınırken bir hata oluştu.");
    } finally {
      setNotifyLoading(false);
    }
  };

  useEffect(() => {
    if (checkTokenExpiration(user.token)) {
      handleLogout();
    }
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!user) return;
      setLoading(true);
      axios.defaults.headers.common['Authorization'] = "Bearer " + user.token;
      try {
        const response = await axios.get(API_URL + "Api/Events");
        if (response && response.data.success && Array.isArray(response.data.data)) {
          const eventsData = response.data.data;
          setEvents(eventsData);
          const count = eventsData.filter(ev => dayjs(ev.createdDate).isAfter(dayjs())).length;
          setFutureEventCount(count);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [user]);

  const [showProfile, setShowProfile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const openProfile = () => setShowProfile(true);
  const closeProfile = () => setShowProfile(false);

  const handleUpdateProfile = (updatedUser) => {
    setIsProcessing(true);
    let holder = updatedUser.role;
    updatedUser.role = updatedUser.role === ADMIN ? 1 : 2;
    axios.post(API_URL + "Users", updatedUser)
      .then(response => {
        updatedUser.role = holder;
        delete updatedUser.password;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setIsProcessing(false);
        closeProfile();
        openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Profil güncellendi.' });
      })
      .catch(error => { setIsProcessing(false); });
  };

  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.removeItem('user');
    axios.defaults.headers.common['Authorization'] = "";
    navigate('/login');
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [completeCount, setCompleteCount] = useState(0);
  const [processCount, setProcessCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [postponedCount, setPostponedCount] = useState(0);
  const [totalTask, setTotalTask] = useState(0);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) {
      axios.get(user.role === ADMIN ? (API_URL + 'TaskItems') : (API_URL + 'TaskItems/ByUserId/' + user.id))
        .then(response => {
          const taskData = Array.isArray(response.data) ? response.data : [];
          setTasks(taskData);
          setTotalTask(taskData.length);
          setCompleteCount(taskData.filter(item => item.task && item.task.status === 4).length);
          setProcessCount(taskData.filter(item => item.task && item.task.status === 3).length);
          setPostponedCount(taskData.filter(item => item.task && item.task.status === 1).length);
          setPendingCount(taskData.filter(item => item.task && item.task.status === 2).length);
        }).catch(err => setError(err));

      axios.get(API_URL + 'Categories').then(res => setCategories(Array.isArray(res.data) ? res.data : []));

      axios.get(API_URL + 'Users').then(res => {
        setUsers(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      });

      axios.get(API_URL + "Notifications/ByUserId/" + user.id).then(res => {
        const rawNotifs = Array.isArray(res.data) ? res.data : [];
        const enriched = rawNotifs.map(notification => {
          const matchedUser = (users || []).find(u => u && u.id === notification.userId);
          if (matchedUser) {
            return {
              ...notification,
              user: {
                id: matchedUser.id,
                name: matchedUser.name,
                surname: matchedUser.surname,
                role: matchedUser.role,
                url: matchedUser.url
              }
            };
          }
          return notification;
        });
        setNotifications(enriched);
      });
    }
  }, [user, users.length]);

  const handleSaveTask = (task, taskUser, taskCategories, statistic) => {
    let taskUserStr = taskUser.map(String).join(',');
    let taskCategoriesStr = taskCategories.map(String).join(',');
    let taskId;

    if (task.id === 0) {
      axios.post(API_URL + "TaskItems", task)
        .then(response => {
          taskId = response.data.id;
          return axios.post(API_URL + "TaskUsers?taskId=" + taskId + "&userIds=" + taskUserStr);
        })
        .then(() => {
          return axios.post(API_URL + "TaskCategories?taskId=" + taskId + "&categorieIds=" + taskCategoriesStr);
        })
        .then(() => {
          return axios.get(API_URL + "TaskItems/" + taskId);
        })
        .then(response => {
          setTasks(prevTasks => [...prevTasks, response.data]);
          setTotalTask(totalTask + 1);
          switch (response.data.task.status) {
            case 1: setPostponedCount(postponedCount + 1); break;
            case 2: setPendingCount(pendingCount + 1); break;
            case 3: setProcessCount(processCount + 1); break;
            case 4: setCompleteCount(completeCount + 1); break;
          };
          openNotificationWithIcon({ type: 'success', title: 'Görev Oluşturma Başarılı', description: '"' + task.title + '" görev oluşturuldu.' });
        })
        .then(() => {
          const filteredUsers = taskUser.filter(aUser => aUser !== user.id);
          const notificationPromises = filteredUsers.map(aUser => {
            let notification = {
              id: 0,
              title: `${user.name} ${user.surname} yeni bir görev verdi.`,
              detail: `${user.name} ${user.surname}, size "${task.title}" adlı yeni bir görev verdi.`,
              date: dayjs().tz('Europe/Istanbul').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
              userId: user.id,
              assignedUserId: aUser
            };
            return axios.post(API_URL + "Notifications/", notification).then(r => { notification.id = r.data.id; return notification; });
          });
          return Promise.all(notificationPromises)
        })
        .catch(error => { openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Görev oluşturulamadı.' }); });
    } else {
      axios.post(API_URL + "TaskItems", task)
        .then(response => {
          return axios.post(API_URL + "TaskUsers?taskId=" + task.id + "&userIds=" + taskUserStr);
        })
        .then(() => {
          return axios.post(API_URL + "TaskCategories?taskId=" + task.id + "&categorieIds=" + taskCategoriesStr);
        })
        .then(() => {
          return axios.get(API_URL + "TaskItems/" + task.id);
        })
        .then(response => {
          setTasks(prevTasks => prevTasks.map(t => t.task.id === response.data.task.id ? { ...response.data } : t));
          if (statistic.isUpdate) {
            setCompleteCount(completeCount + statistic.completeCount);
            setProcessCount(processCount + statistic.processCount);
            setPendingCount(pendingCount + statistic.pendingCount);
            setPostponedCount(postponedCount + statistic.postponedCount);
          }
          openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Görev güncellendi.' });
        })
        .then(() => {
          let tempTaskUser = taskUser[0];
          const filteredUsers = tempTaskUser.filter(aUser => aUser !== user.id);
          const notificationPromises = filteredUsers.map(aUser => {
            let notification = {
              id: 0,
              title: `${user.name} ${user.surname} görevini güncelledi.`,
              detail: `${user.name} ${user.surname}, "${task.title}" adlı görevi güncelledi.`,
              date: dayjs().tz('Europe/Istanbul').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
              userId: user.id,
              assignedUserId: aUser
            };
            return axios.post(API_URL + "Notifications/", notification).then(r => { notification.id = r.data.id; return notification; });
          });
          return Promise.all(notificationPromises)
        })
        .catch(error => { openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Görev güncellenemedi.' }); });
    }
  };

  const deleteTask = (task) => {
    axios.delete(API_URL + "TaskItems/" + task.id).then(() => {
      setTasks(prevTasks => prevTasks.filter(item => item.task.id !== task.id));
      if (task.status === 1) setPostponedCount(postponedCount - 1);
      else if (task.status === 2) setPendingCount(pendingCount - 1);
      else if (task.status === 3) setProcessCount(processCount - 1);
      else if (task.status === 4) setCompleteCount(completeCount - 1);
      setTotalTask(totalTask - 1);
      openNotificationWithIcon({ type: 'info', title: 'Silindi', description: 'Görev silindi.' });
    }).catch(error => { openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Silinemedi.' }); });
  };

  const handleSaveUser = (usr) => {
    if (usr.id === 0) {
      axios.post(API_URL + "Users", usr).then(r => axios.get(API_URL + "Users/" + r.data.id)).then(r => {
        setUsers(p => [...p, r.data]); openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Kullanıcı eklendi.' });
      }).catch(e => openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Eklenemedi.' }));
    } else {
      axios.post(API_URL + "Users", usr).then(r => {
        setUsers(p => p.map(u => u.id === usr.id ? { ...usr } : u)); openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Güncellendi.' });
      }).catch(e => openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Güncellenemedi.' }));
    }
  };
  const deleteUser = (userId) => {
    axios.delete(API_URL + "Users/" + userId).then(() => {
      setUsers(p => p.filter(u => u.id !== userId)); openNotificationWithIcon({ type: 'info', title: 'Silindi', description: 'Kullanıcı silindi.' });
    }).catch(e => openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Silinemedi.' }));
  }
  const addTask = (category) => {
    if (categories.every(c => c.name.toLowerCase() !== category.toLowerCase())) {
      axios.post(API_URL + "Categories", { "name": category }).then(r => {
        setCategories(p => [...p, r.data]); openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Kategori eklendi.' });
      });
    }
  };

  const [userModalVisible, setUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const handleAddUser = () => { setEditingUser(null); setUserModalVisible(true); };
  const handleEditUser = (usr) => { setEditingUser(usr); setUserModalVisible(true); };
  const handleCloseUserModal = () => { setEditingUser(null); setUserModalVisible(false); };
  const handleAddEvent = () => setIsEventModalOpen(true);
  const handleCloseEventModal = () => setIsEventModalOpen(false);

  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const handleAddTask = () => { setEditingTask(null); setTaskModalVisible(true); };
  const handleEditTask = (task) => { setEditingTask(task); setTaskModalVisible(true); };
  const handleCloseModal = () => { setEditingTask(null); setTaskModalVisible(false); };

  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [modalType, setModalType] = useState();
  const handleOpenNotificationModal = (type) => { setModalType(type); setNotificationModalVisible(true); };
  const handleCloseNotificationModal = () => setNotificationModalVisible(false);
  const sendCustomNotification = (notification, assignedUsers) => {
    const notificationPromises = assignedUsers.map(aUserId => {
      let newNotification = { ...notification, assignedUserId: aUserId };
      return axios.post(API_URL + "Notifications/", newNotification);
    });

    Promise.all(notificationPromises)
      .then(() => {
        openNotificationWithIcon({ type: 'success', title: 'Başarılı', description: 'Duyuru gönderildi.' });
      })
      .catch(error => {
        openNotificationWithIcon({ type: 'error', title: 'Hata', description: 'Duyuru gönderilemedi.' });
      });
  };

  const [filter, setFilter] = useState([1, 2, 3]);
  const statisticFilter = (filters) => setFilter(filters);

  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Özel CSS Stillerini Enjekte Ediyoruz */}
      <style>{mobileStyles}</style>

      {/* ----------------- HEADER ----------------- */}
      <Header style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: isMobile ? 'auto' : '10vh',
        padding: isMobile ? '12px 4px 2px 4px' : '0 20px',
        color: 'white',
        flexShrink: 0,
        lineHeight: 'normal',
        transition: 'all 0.3s',
      }}>
        <Row gutter={isMobile ? 0 : 16} align="middle">

          <Col span={isMobile ? 12 : 12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <div className="user" style={{
              display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', marginLeft: isMobile ? '0' : '-15px', gap: isMobile ? '2px' : '0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Notification data={notifications} users={users} />
                <EventModal open={isEventModalOpen} onClose={handleCloseEventModal} maskClosable={true} />
                <Divider type="vertical" style={{ borderColor: 'rgba(255,255,255,0.3)', margin: isMobile ? '0 4px' : '0 8px' }} />

                <Button size="large" type="text" onClick={openProfile} style={{ display: 'flex', alignItems: 'center', height: 'auto', padding: isMobile ? '0 2px' : '10px 15px' }}>
                  <Avatar shape="square" style={{ backgroundColor: '#78bf9b', verticalAlign: 'middle', marginTop: isMobile ? '0' : '-5px' }} size={isMobile ? 36 : "large"} icon={<UserOutlined />} src={user.url} />
                  <div style={{ color: 'white', fontWeight: '500', fontSize: isMobile ? '13px' : '16px', marginLeft: isMobile ? '8px' : '8px', textAlign: 'left', lineHeight: '1.2' }}>
                    {user.name} {!isMobile && user.surname}
                  </div>
                </Button>

                {!isMobile && (user.role === ADMIN || user.id === 3) && (
                  <>
                    <Divider type="vertical" />
                    <div className="tableSegment">
                      <Segmented options={[{ value: 'tasks', icon: <ScheduleOutlined style={{ fontSize: '18px' }} /> }, { value: 'users', icon: <ContactsOutlined style={{ fontSize: '18px' }} /> }]} value={table} onChange={setTable} />
                    </div>
                  </>
                )}
              </div>

              <div style={{ marginLeft: isMobile ? '0' : '0', marginTop: isMobile ? '-2px' : '0', transform: isMobile ? 'scale(0.8)' : 'none', transformOrigin: 'left center' }}>
                {!isMobile && <Divider type="vertical" />}
                {!isMobile && <Statistics data={{ completeCount, processCount, pendingCount, postponedCount, totalTask }} statisticFilter={statisticFilter} />}
              </div>
            </div>
          </Col>



          <Col span={isMobile ? 12 : 12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: isMobile ? '4px' : '16px', paddingRight: '1px' }}>
            <div className="addButton" style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                icon={table === 'tasks' ? <FileAddOutlined style={{ fontSize: isMobile ? '16px' : undefined }} /> : <UserAddOutlined style={{ fontSize: isMobile ? '16px' : undefined }} />}
                size="large" type="text"
                style={{
                  color: 'white',
                  fontWeight: '500',
                  fontSize: isMobile ? '9px' : '16px',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  height: 'auto',
                  gap: isMobile ? '0' : '8px',
                  padding: isMobile ? '4px 8px' : '4px 15px',
                  backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  borderRadius: isMobile ? '12px' : '0',
                  boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                  border: isMobile ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  minWidth: isMobile ? '45px' : 'auto'
                }}
                onClick={table === 'tasks' ? handleAddTask : handleAddUser}
              >
                {isMobile ? (table === 'tasks' ? 'Görev' : 'Üye') : (table === 'tasks' ? 'Görev Ekle' : 'Kullanıcı Ekle')}
              </Button>
            </div>

            <div style={{ position: 'relative' }}>
              <Button
                icon={<CalendarOutlined style={{ fontSize: isMobile ? '16px' : undefined }} />}
                size="large" type="text"
                style={{
                  color: 'white',
                  fontWeight: '500',
                  fontSize: isMobile ? '9px' : '16px',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '0' : '4px',
                  padding: isMobile ? '4px 8px' : '4px 24px',
                  height: 'auto',
                  backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  borderRadius: isMobile ? '12px' : '0',
                  boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                  border: isMobile ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  minWidth: isMobile ? '45px' : 'auto'
                }}
                onClick={handleAddEvent}
              >
                {!isMobile && 'Etkinlikler'} {isMobile && 'Etkinlik'}
              </Button>
              {futureEventCount > 0 && (
                <div style={{ position: 'absolute', top: isMobile ? '-4px' : '6px', right: isMobile ? '-4px' : '1px', backgroundColor: '#ff4d4f', color: 'white', borderRadius: '50%', width: isMobile ? '14px' : '20px', height: isMobile ? '14px' : '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: isMobile ? '9px' : '12px', fontWeight: 'bold', zIndex: 1 }}>
                  {futureEventCount}
                </div>
              )}
            </div>
            <div className="logout">
              <Button onClick={handleLogout} size="large" type="text" style={{
                color: 'white',
                fontWeight: '500',
                fontSize: '16px',
                padding: isMobile ? '4px 8px' : '15px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '0' : '4px',
                backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                borderRadius: isMobile ? '12px' : '0',
                boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                border: isMobile ? '1px solid rgba(255,255,255,0.2)' : 'none',
                minWidth: isMobile ? '45px' : 'auto',
                height: 'auto'
              }}>
                {!isMobile && "Çıkış Yap"} <LogoutOutlined style={{ fontSize: isMobile ? '16px' : undefined }} />
                {isMobile && <span style={{ fontSize: '9px' }}>Çıkış</span>}
              </Button>
            </div>
          </Col>
        </Row>
        {isMobile && (
          <div style={{ width: '100%', marginTop: '8px', paddingBottom: '0px' }}>
            <Statistics data={{ completeCount, processCount, pendingCount, postponedCount, totalTask }} statisticFilter={statisticFilter} isMobile={isMobile} />
          </div>
        )}
      </Header>

      <Content style={{ flex: 1, margin: isMobile ? '8px 4px' : '24px 16px', background: colorBgContainer, borderRadius: borderRadiusLG, overflow: 'hidden', position: 'relative' }}>
        <Profile shown={showProfile} onClose={closeProfile} onSave={handleUpdateProfile} loading={isProcessing} />
        <TaskModal users={users} categories={categories} onOpen={taskModalVisible} data={editingTask} onClose={handleCloseModal} onSave={handleSaveTask} />
        <UserModal data={editingUser} onOpen={userModalVisible} onClose={handleCloseUserModal} onSave={handleSaveUser} />
        <NotificationModal users={users} onOpen={notificationModalVisible} onClose={handleCloseNotificationModal} onSave={sendCustomNotification} type={modalType} />

        <div className='main-table' style={{ background: colorBgContainer, minHeight: '100%', borderRadius: borderRadiusLG }}>
          <div className="mobile-scroll-container">
            {table === "tasks" ? (
              !loading && !error && (
                <Tasks
                  addTask={addTask}
                  users={users}
                  categories={categories}
                  tasks={tasks}
                  onEditTask={handleEditTask}
                  deleteTask={deleteTask}
                  sFilter={filter}
                  setNotifications={setNotifications}
                />
              )
            ) : (
              <Users userList={users} onEditUser={handleEditUser} deleteUser={deleteUser} />
            )}
          </div>

          {(user.role === ADMIN || user.id === 3) && (
            <FloatButton.Group trigger="click" type="default" tooltip={"Duyuru Yap"} style={{ bottom: '20px', right: '20px' }} icon={<NotificationOutlined />}>
              <FloatButton type="primary" tooltip={"Kendi Adına"} icon={<UserOutlined />} onClick={() => handleOpenNotificationModal(1)} />
              <FloatButton type="primary" tooltip={"Sistem Adına"} icon={<RobotOutlined />} onClick={() => handleOpenNotificationModal(2)} />
            </FloatButton.Group>
          )}
        </div>
      </Content>

      <Modal
        title={<span><BellOutlined /> Bildirimleri Aktif Et</span>}
        open={isNotifyModalVisible}
        onCancel={() => setIsNotifyModalVisible(false)}
        footer={[
          <Button key="later" onClick={() => setIsNotifyModalVisible(false)}>Daha Sonra</Button>,
          <Button key="allow" type="primary" loading={notifyLoading} onClick={handleAllowNotifications}>İzin Ver ve Kaydet</Button>,
        ]}
      >
        <div style={{ textAlign: 'center' }}>
          <Text>Görevleriniz ve etkinliklerinizle ilgili anlık güncellemeleri kilit ekranınızda görmek ister misiniz?</Text>
        </div>
      </Modal>
    </Layout>
  );
}

export default HomePage;